<template>
  <div class="page-container">
    <div class="page-header">
      <h2>基础规则配置</h2>
      <p>配置系统的基础规则，用于找出与核心股东关联的疑似普通股东</p>
    </div>

    <!-- 操作区域 -->
    <div class="action-section">
      <el-button type="primary" @click="handleAdd">添加规则</el-button>
      <el-button type="danger" @click="handleDeleteBatch" :disabled="selectedRows.length === 0">批量删除</el-button>
      <el-button @click="fetchData">刷新</el-button>
    </div>

    <!-- 数据表格区域 -->
    <div class="table-section">
      <el-table :data="tableData" border stripe style="width: 100%" @selection-change="handleSelectionChange" v-loading="loading">
        <el-table-column type="selection" width="55" />
        <el-table-column prop="rule_id" label="规则编号" width="180">
          <template #default="{ row }">
            <span class="rule-id-text">{{ row.rule_id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="rule_type" label="规则类型" width="100">
          <template #default="{ row }">
            <el-tag :type="getRuleTypeTag(row.rule_type)">{{ getRuleTypeText(row.rule_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="match_condition" label="匹配条件" width="100">
          <template #default="{ row }">
            <el-tag>{{ getMatchConditionText(row.match_condition) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="match_value" label="匹配值" min-width="150" show-overflow-tooltip />
        <el-table-column prop="core_name" label="对应核心股东" min-width="150">
          <template #default="{ row }">
            <span v-if="row.core_name">{{ row.core_name }}</span>
            <span v-else class="text-muted">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="confidence" label="置信度" width="80">
          <template #default="{ row }">
            {{ row.confidence }}%
          </template>
        </el-table-column>
        <el-table-column prop="priority" label="优先级" width="80">
          <template #default="{ row }">
            <el-tag :type="getPriorityTag(row.priority)">{{ getPriorityText(row.priority) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="is_enabled" label="状态" width="70">
          <template #default="{ row }">
            <el-tag :type="row.is_enabled ? 'success' : 'info'">
              {{ row.is_enabled ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="desc" label="规则描述" min-width="200" show-overflow-tooltip />
        <el-table-column label="操作" width="180">
          <template #default="{ row }">
            <el-button size="small" type="primary" @click="handleEdit(row)">编辑</el-button>
            <el-button size="small" type="danger" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        >
        </el-pagination>
      </div>
    </div>

    <!-- 编辑/添加弹窗 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="60%" @close="resetForm">
      <el-form :model="form" :rules="rules" ref="formRef" label-width="140px">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="规则类型" prop="rule_type">
              <el-select v-model="form.rule_type" placeholder="请选择规则类型" style="width: 100%" @change="handleRuleTypeChange">
                <el-option label="地址匹配" value="address" />
                <el-option label="身份证匹配" value="id_card" />
                <el-option label="手机号匹配" value="phone" />
                <el-option label="交易行为匹配" value="trade" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="匹配条件" prop="match_condition">
              <el-select v-model="form.match_condition" placeholder="请选择匹配条件" style="width: 100%">
                <el-option label="包含" value="contains" />
                <el-option label="精确匹配" value="exact" />
                <el-option label="模糊匹配" value="fuzzy" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="匹配值" prop="match_value">
              <el-input v-model="form.match_value" :placeholder="getMatchValuePlaceholder" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="对应核心股东" prop="core_shareholder_id">
              <HolderSuggestInput
                v-model="form.core_shareholder_id"
                type="core"
                placeholder="汉字/拼音/首字母"
                style="width: 100%"
                @select="(item) => { form.core_name = item.label || item.name || '' }"
              />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="置信度(%)" prop="confidence">
              <el-slider v-model="form.confidence" :min="0" :max="100" show-input />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="优先级" prop="priority">
              <el-select v-model="form.priority" placeholder="请选择优先级" style="width: 100%">
                <el-option label="高" value="high" />
                <el-option label="中" value="medium" />
                <el-option label="低" value="low" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-form-item label="启用状态" prop="is_enabled">
          <el-switch v-model="form.is_enabled" />
        </el-form-item>
        
        <el-form-item label="规则描述" prop="desc">
          <el-input v-model="form.desc" type="textarea" :rows="3" placeholder="请输入规则描述" />
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'

// 表格数据
const tableData = ref([])
const loading = ref(false)
const submitLoading = ref(false)


// 分页配置
const pagination = reactive({
  currentPage: 1,
  pageSize: 10,
  total: 0
})

// 选中的行
const selectedRows = ref([])

// 弹窗相关
const dialogVisible = ref(false)
const formRef = ref(null)
const isEdit = ref(false)

const form = reactive({
  rule_id: '',
  rule_type: 'address',
  match_condition: 'contains',
  match_value: '',
  core_shareholder_id: '',
  core_name: '',
  confidence: 50,
  priority: 'medium',
  is_enabled: true,
  desc: ''
})

// 表单验证规则（核心股东必选）
const rules = reactive({
  rule_type: [
    { required: true, message: '请选择规则类型', trigger: 'change' }
  ],
  match_condition: [
    { required: true, message: '请选择匹配条件', trigger: 'change' }
  ],
  match_value: [
    { required: true, message: '请输入匹配值', trigger: 'blur' }
  ],
  core_shareholder_id: [
    { required: true, message: '请选择对应核心股东', trigger: 'change' }
  ],
  priority: [
    { required: true, message: '请选择优先级', trigger: 'change' }
  ]
})

// 计算弹窗标题
const dialogTitle = computed(() => isEdit.value ? '编辑规则' : '添加规则')

// 根据规则类型获取匹配值提示
const getMatchValuePlaceholder = computed(() => {
  const placeholders = {
    'address': '请输入地址关键词，如：上海市浦东新区',
    'id_card': '请输入身份证号前6位，如：310000',
    'phone': '请输入手机号前3位或完整号码，如：138、139',
    'trade': '请输入交易行为描述，如：大额交易、频繁操作'
  }
  return placeholders[form.rule_type] || '请输入匹配值'
})

// 获取规则类型显示文本
const getRuleTypeText = (type) => {
  const map = {
    'address': '地址',
    'id_card': '身份证',
    'phone': '手机号',
    'trade': '交易行为'
  }
  return map[type] || type
}

// 获取规则类型标签类型
const getRuleTypeTag = (type) => {
  const map = {
    'address': 'warning',
    'id_card': 'danger',
    'phone': 'success',
    'trade': 'info'
  }
  return map[type] || 'info'
}

// 获取匹配条件显示文本
const getMatchConditionText = (condition) => {
  const map = {
    'contains': '包含',
    'exact': '精确匹配',
    'fuzzy': '模糊匹配'
  }
  return map[condition] || condition
}

// 获取优先级显示文本
const getPriorityText = (priority) => {
  const map = {
    'high': '高',
    'medium': '中',
    'low': '低'
  }
  return map[priority] || priority
}

// 获取优先级标签类型
const getPriorityTag = (priority) => {
  const map = {
    'high': 'danger',
    'medium': 'warning',
    'low': 'info'
  }
  return map[priority] || 'info'
}

// 处理规则类型变化（可选：可以根据类型自动调整匹配条件）
const handleRuleTypeChange = (value) => {
  // 可以在这里根据规则类型自动设置默认值
  if (value === 'id_card') {
    form.match_condition = 'exact'
  } else if (value === 'phone') {
    form.match_condition = 'contains'
  }
}

// 获取数据
const fetchData = async () => {
  loading.value = true
  try {
    const response = await axios.get('/api/basic_rule/', { withCredentials: true })
    console.log('规则列表响应:', response.data)
    if (response.data.status === 'ok') {
      tableData.value = response.data.data || []
      pagination.total = tableData.value.length
    } else {
      ElMessage.error(response.data.message || '获取数据失败')
    }
  } catch (error) {
    console.error('获取数据失败:', error)
    ElMessage.error(error.response?.data?.message || '获取数据失败，请检查后端服务')
  } finally {
    loading.value = false
  }
}

// 重置表单
const resetForm = () => {
  form.rule_id = ''
  form.rule_type = 'address'
  form.match_condition = 'contains'
  form.match_value = ''
  form.core_shareholder_id = ''
  form.core_name = ''
  form.confidence = 50
  form.priority = 'medium'
  form.is_enabled = true
  form.desc = ''
}

// 添加规则
const handleAdd = () => {
  isEdit.value = false
  resetForm()
  dialogVisible.value = true
}

// 编辑规则
const handleEdit = (row) => {
  isEdit.value = true
  form.rule_id = row.rule_id
  form.rule_type = row.rule_type
  form.match_condition = row.match_condition
  form.match_value = row.match_value
  form.core_shareholder_id = row.core_shareholder_id || ''
  form.core_name = row.core_name || ''
  form.confidence = row.confidence
  form.priority = row.priority
  form.is_enabled = row.is_enabled
  form.desc = row.desc || ''
  dialogVisible.value = true
}

// 删除规则
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(`确定要删除规则 "${row.rule_id}" 吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const response = await axios.delete(`/api/basic_rule/delete/${row.rule_id}/`, { withCredentials: true })
    if (response.data.status === 'ok') {
      ElMessage.success('删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除失败:', error)
      ElMessage.error('删除失败')
    }
  }
}

// 批量删除
const handleDeleteBatch = async () => {
  if (selectedRows.value.length === 0) return
  
  try {
    await ElMessageBox.confirm(`确定要删除选中的 ${selectedRows.value.length} 条规则吗？`, '提示', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      type: 'warning'
    })
    
    const ruleIds = selectedRows.value.map(row => row.rule_id)
    const response = await axios.post('/api/basic_rule/batch_delete/', { rule_ids: ruleIds })
    if (response.data.status === 'ok') {
      ElMessage.success('批量删除成功')
      fetchData()
    } else {
      ElMessage.error(response.data.message || '删除失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('批量删除失败:', error)
      ElMessage.error('批量删除失败')
    }
  }
}

// 提交表单
const handleSubmit = async () => {
  formRef.value.validate(async (valid) => {
    if (valid) {
      submitLoading.value = true
      try {
        let response
        if (isEdit.value) {
          response = await axios.put(`/api/basic_rule/update/${form.rule_id}/`, form, { withCredentials: true })
        } else {
          response = await axios.post('/api/basic_rule/create/', form, { withCredentials: true })
        }
        
        if (response.data.status === 'ok') {
          ElMessage.success(isEdit.value ? '编辑成功' : '添加成功')
          dialogVisible.value = false
          fetchData()
        } else {
          ElMessage.error(response.data.message || '操作失败')
        }
      } catch (error) {
        console.error('操作失败:', error)
        ElMessage.error('操作失败')
      } finally {
        submitLoading.value = false
      }
    } else {
      ElMessage.error('请填写完整信息')
    }
  })
}

// 选择行变化
const handleSelectionChange = (val) => {
  selectedRows.value = val
}

// 分页相关方法
const handleSizeChange = (val) => {
  pagination.pageSize = val
}
const handleCurrentChange = (val) => {
  pagination.currentPage = val
}

// 页面加载时获取数据
onMounted(() => {
  fetchData()
})
</script>

<style scoped>
.page-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #606266;
}

.action-section {
  margin-bottom: 20px;
}

.table-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.dialog-footer {
  text-align: right;
}

.rule-id-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #909399;
}

.text-muted {
  color: #c0c4cc;
}
</style>
